/**
 * API Wrapper Utility
 *
 * Generic wrapper for API calls with consistent error handling.
 * Eliminates duplicate try-catch blocks across service files.
 *
 * @module utils/apiWrapper
 */

import { AxiosResponse } from 'axios';
import { handleApiError } from '@shared/services/apiClient';

/**
 * Generic wrapper for API calls with consistent error handling
 *
 * @template T - Response data type
 * @param operation - Human-readable operation description (e.g., "fetch campaign")
 * @param apiFunction - Axios API call function
 * @returns Promise with response data
 *
 * @example
 * ```typescript
 * // Simple GET request
 * export const fetchCampaignById = (id: string) =>
 *   apiCall(`fetch campaign ${id}`, () =>
 *     apiClient.get<CampaignResponse>(`/campaigns/${id}`)
 *   );
 *
 * // POST request with data
 * export const createCampaign = (data: CampaignCreateRequest) =>
 *   apiCall('create campaign', () =>
 *     apiClient.post<CampaignResponse>('/campaigns', data)
 *   );
 *
 * // DELETE request
 * export const deleteCampaign = (id: string) =>
 *   apiCall(`delete campaign ${id}`, () =>
 *     apiClient.delete<SuccessResponse>(`/campaigns/${id}`)
 *   );
 * ```
 */
export async function apiCall<T>(
  operation: string,
  apiFunction: () => Promise<AxiosResponse<T>>
): Promise<T> {
  try {
    const response = await apiFunction();
    return response.data;
  } catch (error) {
    const message = handleApiError(error);
    console.error(`Failed to ${operation}:`, message);
    throw new Error(message);
  }
}
